import { getEntityclassByExternalId, getAttributesetByExternalId } from './eav.js';
import type { Upload } from '@fibl/oxs-common';
import type { EAV, SearchRepo } from '@fibl/zdb-common';
import { search, getEntityById } from './search.js';
import type { Pagination } from './search.js';
import * as EntityRepo from './entity.js';
import type { UploadModel } from '@fibl/oxs-common';
import { updateEntity } from './eav';

function readFile(file: File): Promise<string> {
	return new Promise((res, rej) => {
		let r = new FileReader();
		r.readAsDataURL(file);
		r.onload = () => res(r.result as string);
		r.onerror = rej;
	});
}

export async function createDocumentFromFile(
	file: File,
	type: Upload.UploadType,
	owner: string,
	viewers?: string[],
	notice = '',
	pub = false,
	displayName?: string,
) {
	const fileContent = await readFile(file);
	return createDocument(
		fileContent,
		file.name,
		type,
		owner,
		displayName || '',
		viewers,
		notice,
		pub,
	);
}

export async function createDocument(
	fileContent: string,
	fileName: string,
	type: Upload.UploadType,
	owner: string,
	displayName: string,
	viewers?: string[],
	notice = '',
	pub = false,
): Promise<EAV.Entity<Upload.Upload>> {
	let ec = await getEntityclassByExternalId('uploaded');
	let as = await getAttributesetByExternalId('uploaded');
	let encoder = new TextEncoder();
	let encodedFile = encoder.encode(fileContent);
	if (encodedFile.length > 32 * 1024 * 1024) {
		throw new Error('File size cannot be above 24MB.');
	}
	let data: Upload.Upload = {
		name: fileName,
		owner,
		type,
		public: pub,
		viewers: viewers || [],
		notice,
		displayName: displayName,
	};
	let documentEntity = await EntityRepo.create(ec.id, as.id, data);
	let attachmentResponse;
	try {
		attachmentResponse = await EntityRepo.setAttachment<Upload.Upload>(
			documentEntity.id,
			documentEntity.rev,
			fileContent,
		);
	} catch (e) {
		await EntityRepo.remove(documentEntity.id);
		throw new Error(`Upload of attachment failed: ${fileName}.`);
	}
	return attachmentResponse;
}

type DocWithFile = EAV.Entity<Upload.Upload> & { file: () => string };

export async function getDocument(id: string, fileName: string): Promise<DocWithFile> {
	let document: any = await getEntityById<Upload.Upload>(id);
	let attachment = await EntityRepo.getAttachment(id);
	if (!attachment) {
		throw new Error(`File with name ${fileName} not found`);
	}
	Object.defineProperty(document, 'file', {
		get() {
			// let dec = new TextDecoder();
			return attachment;
		},
	});
	return document;
}

type DocFilter = {
	owner: string;
};

export async function searchDocuments(
	filter: DocFilter,
	pagination: Pagination,
	sorting: string,
): Promise<EAV.Entity<Upload.Upload>[]> {
	let ec = await getEntityclassByExternalId('uploaded');
	let $and: any[] = [];
	$and.push({
		field: '/data/owner',
		comparison: 'eq',
		value: filter.owner || '-',
	});
	return search<Upload.Upload>(ec.id, { $and }, pagination, sorting);
}

export async function update(ent: UploadModel) {
	return updateEntity(ent);
}

export async function changeFileUpload(file: File, id: string, rev: number, fileName: string) {
	const fileContent = await readFile(file);
	let attachmentResponse;
	try {
		attachmentResponse = await EntityRepo.setAttachment<Upload.Upload>(id, rev, fileContent);
	} catch (e) {
		await EntityRepo.remove(id);
		throw new Error(`Upload of attachment failed: ${fileName}.`);
	}
	return attachmentResponse;
}

export async function searchTypeDocs(
	type: string,
	pagination: Pagination,
	sorting: string,
	owner?: string,
) {
	let ec = await getEntityclassByExternalId('uploaded');
	const filter: SearchRepo.SearchFilter = {
		$and: [
			{
				field: '/data/type',
				comparison: 'eq',
				value: type,
			},
		],
	};
	if (owner) {
		filter.$and.push({
			field: '/data/owner',
			comparison: 'eq',
			value: owner,
		});
	}
	return search<Upload.Upload>(ec.id, filter, pagination, sorting);
}
